import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, Col, Input, Row } from "reactstrap";

import { H4 } from "../../../../AbstractElements";
import Graph from "../../../../CommonElements/Graph";
import {
  fetchReportData,
  fetchAddedReportData,
  fetchCertifiedPreFoundational,
  fetchCertifiedFoundational,
  fetchCertifiedAdvanced,
  fetchCertifiedExpert,
} from "../../../../ReduxToolkit/Reducers/ReportSlice";
import { RootState } from "../../../../ReduxToolkit/Store";
import {
  Added,
  Advanced,
  AllLevels,
  CertifiedPercentage,
  Expert,
  Foundational,
  HeadCount,
  PreFoundational,
} from "../../../../utils/Constant";
import {
  headCountOptions,
  proficiencyLevelKeyValue,
} from "../../../../utils/helper/helper";

interface FilterValues {
  domain?: string[];
  fy?: string[];
  sbu?: string[];
  [key: string]: any;
}

interface CDPGraphTypeProps {
  filterValues: FilterValues;
  domainList: any[];
  sbuList: any[];
}

const CDPGraphType = ({ filterValues }: CDPGraphTypeProps) => {
  const [selectedAddedCountDrop, setSelectedAddedCountDrop] = useState("all");

  const dispatch = useDispatch();
  const {
    reportData,
    addedReportData,
    certifiedPreFoundationalData,
    certifiedFoundationalData,
    certifiedAdvancedData,
    certifiedExpertData,
  } = useSelector((state: RootState) => state.report);

  const domain_id = filterValues.domain?.[0];
  const financial_year = filterValues.fy?.[0]
    ? filterValues.fy[0].split("-")[0]
    : undefined;
  const sbu = filterValues.sbu?.[0];
  const canCallAPIs = !!(domain_id && financial_year && sbu);

  useEffect(() => {
    if (!canCallAPIs) return;
    const headCountParams: any = { domain_id, financial_year, sbu };
    dispatch(fetchReportData(headCountParams) as any);
  }, [dispatch, domain_id, financial_year, sbu, canCallAPIs]);

  useEffect(() => {
    if (!canCallAPIs) return;
    const addedParams: any = { domain_id, financial_year, sbu };
    if (selectedAddedCountDrop !== "all") {
      addedParams.level_id = selectedAddedCountDrop;
    }
    dispatch(fetchAddedReportData(addedParams) as any);
  }, [
    dispatch,
    selectedAddedCountDrop,
    domain_id,
    financial_year,
    sbu,
    canCallAPIs,
  ]);

  useEffect(() => {
    if (!canCallAPIs) return;
    dispatch(
      fetchCertifiedPreFoundational({ domain_id, financial_year, sbu }) as any
    );
    dispatch(
      fetchCertifiedFoundational({ domain_id, financial_year, sbu }) as any
    );
    dispatch(fetchCertifiedAdvanced({ domain_id, financial_year, sbu }) as any);
    dispatch(fetchCertifiedExpert({ domain_id, financial_year, sbu }) as any);
  }, [dispatch, domain_id, financial_year, sbu, canCallAPIs]);

  const series1 = [
    {
      name: HeadCount,
      data: reportData.map(item => item.value),
    },
  ];

  const series2 = [
    {
      name: Added,
      data: addedReportData.map(item => item.value),
    },
  ];

  const series3 = [
    {
      name: PreFoundational,
      data: certifiedPreFoundationalData.map(item => item.value),
    },
  ];

  const series4 = [
    {
      name: Foundational,
      data: certifiedFoundationalData.map(item => item.value),
    },
  ];

  const series5 = [
    {
      name: Advanced,
      data: certifiedAdvancedData.map(item => item.value),
    },
  ];

  const series6 = [
    {
      name: Expert,
      data: certifiedExpertData.map(item => item.value),
    },
  ];
  const updatedOptions = {
    ...headCountOptions,
    dataLabels: {
      enabled: false,
      formatter: function (val) {
        return val.toFixed(2);
      },
      offsetY: -15,
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return val.toFixed(2);
        },
      },
    },
  };

  return (
    <>
      <Row>
        <Col lg="6">
          <Card>
            <CardBody>
              <Row>
                <Col>
                  <H4 className="mt-2">{HeadCount}</H4>
                </Col>
              </Row>
              <Graph series={series1} options={updatedOptions} />
            </CardBody>
          </Card>
        </Col>
        <Col lg="6">
          <Card>
            <CardBody>
              <Row>
                <Col>
                  <H4 className="mt-2">{Added}</H4>
                </Col>
                <Col lg="8">
                  <Input
                    type="select"
                    value={selectedAddedCountDrop}
                    onChange={e => {
                      setSelectedAddedCountDrop(e.target.value);
                    }}
                  >
                    <option key={0} value={"all"}>
                      {AllLevels}
                    </option>
                    {proficiencyLevelKeyValue?.map(option => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>
              <Graph series={series2} options={updatedOptions} />
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Card>
        <CardBody>
          <H4 className="mb-3">{CertifiedPercentage}</H4>
          <Row>
            <Col lg="6">
              <Card>
                <CardBody>
                  <H4 className="mb-3">{PreFoundational}</H4>
                  <Graph series={series3} options={updatedOptions} />
                </CardBody>
              </Card>
            </Col>
            <Col lg="6">
              <Card>
                <CardBody>
                  <H4 className="mb-3">{Foundational}</H4>
                  <Graph series={series4} options={updatedOptions} />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col lg="6">
              <Card>
                <CardBody>
                  <H4 className="mb-3">{Advanced}</H4>
                  <Graph series={series5} options={updatedOptions} />
                </CardBody>
              </Card>
            </Col>
            <Col lg="6">
              <Card>
                <CardBody>
                  <H4 className="mb-3">{Expert}</H4>
                  <Graph series={series6} options={updatedOptions} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  );
};

export default CDPGraphType;
